import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../components/ConfirmDialog';
import { AdminPageLayout, Section, Card, Badge } from '../../components';
import {
  AttributeStack,
  AttributeGrid
} from '../../components/admin/patterns';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  ViewAction,
  CreateAction,
  CopyAction
} from '../../components/listing/v2/ListingAction';
import {
  Target,
  Trophy,
  Award,
  CheckCircle,
  XCircle,
  ChevronRight,
  TrendingUp,
  BookOpen,
  MoveRight,
  Zap,
  Shield,
  CircleUser,
  Focus,
  MapPin,
  Brain,
  Activity,
  Lock
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const SkillTemplateManagement = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [systemTemplates, setSystemTemplates] = useState([]);
  const [clubSkills, setClubSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    search: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Determine if we're in club context or platform context
  const isClubAdmin = window.location.pathname === '/admin/skills' || window.location.pathname === '/coach/skills';
  const basePath = isClubAdmin ? '/admin/skills' : '/admin/skill-templates';

  const SKILL_CATEGORIES = [
    { value: 'passing', label: 'Passing' },
    { value: 'shooting', label: 'Shooting' },
    { value: 'dribbling', label: 'Dribbling' },
    { value: 'defending', label: 'Defending' },
    { value: 'heading', label: 'Heading' },
    { value: 'ball_control', label: 'Ball Control' },
    { value: 'positioning', label: 'Positioning' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'mental', label: 'Mental' }
  ];

  const DIFFICULTY_LEVELS = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'expert', label: 'Expert' }
  ];

  const loadTemplatesAndSkills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const activeOnly = filters.status === 'active';

      // Determine if we're in club context (club admin page) or platform context
      const isClubContext = isClubAdmin;

      // Load system templates always
      const systemData = await apiService.getSkillTemplates(activeOnly);
      setSystemTemplates(systemData || []);

      // Only load club skills if in club context
      if (isClubContext) {
        const clubData = await apiService.listClubSkills(activeOnly);
        setClubSkills(clubData || []);
      } else {
        setClubSkills([]);
      }
    } catch (err) {
      setError(err.message);
      showError(`Error loading skills: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filters.status, showError, isClubAdmin]);

  useEffect(() => {
    loadTemplatesAndSkills();
  }, [loadTemplatesAndSkills]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleCopyTemplate = async (template) => {
    try {
      await apiService.copySkillTemplate(template.id);
      showSuccess(`Skill template "${template.name}" copied to club skills.`);
      await loadTemplatesAndSkills();
    } catch (err) {
      showError(`Error copying skill template: ${err.message}`);
    }
  };

  const handleDeleteSkill = async (skill) => {
    setIsDeleting(true);

    try {
      // Determine if this is a system template or club skill based on modal data
      const isTemplate = showDeleteModal.isTemplate;

      if (isTemplate) {
        await apiService.deleteSkillTemplate(skill.id);
        showSuccess(`Skill template "${skill.name}" deleted successfully.`);
      } else {
        await apiService.deleteClubSkill(skill.id);
        showSuccess(`Club skill "${skill.name}" deleted successfully.`);
      }

      setShowDeleteModal(null);
      await loadTemplatesAndSkills();
    } catch (err) {
      showError(`Error deleting: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const applyFilters = (items) => {
    return items.filter(item => {
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        if (!item.name.toLowerCase().includes(searchTerm) &&
          !(item.description && item.description.toLowerCase().includes(searchTerm))) {
          return false;
        }
      }
      if (filters.category !== 'all') {
        if (item.category !== filters.category) return false;
      }
      return true;
    });
  };

  const filteredSystemTemplates = applyFilters(systemTemplates);
  const filteredClubSkills = applyFilters(clubSkills);

  const getCategoryLabel = (category) => {
    const cat = SKILL_CATEGORIES.find(c => c.value === category);
    return cat ? cat.label : category || 'Uncategorized';
  };

  const getDifficultyLabel = (level) => {
    const diff = DIFFICULTY_LEVELS.find(d => d.value === level);
    return diff ? diff.label : level || 'N/A';
  };

  const getSkillIcon = (category) => {
    const icons = {
      passing: <MoveRight size={32} />,
      shooting: <Target size={32} />,
      dribbling: <Zap size={32} />,
      defending: <Shield size={32} />,
      heading: <CircleUser size={32} />,
      ball_control: <Focus size={32} />,
      positioning: <MapPin size={32} />,
      fitness: <Activity size={32} />,
      mental: <Brain size={32} />
    };
    return icons[category] || <Trophy size={32} />;
  };

  const isAlreadyCopied = (templateId) => {
    return clubSkills.some(skill => skill.template_id === templateId);
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]
        },
        {
          name: 'category',
          label: 'Category',
          options: [
            { value: 'all', label: 'All Categories' },
            ...SKILL_CATEGORIES
          ]
        }
      ]}
      activeFilters={{ status: filters.status, category: filters.category }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ status: 'all', category: 'all', search: '' })}
    />
  );

  const renderSkillItem = (item, isSystemTemplate = false) => {
    const alreadyCopied = isSystemTemplate && isAlreadyCopied(item.id);

    return (
      <ListingItem
        key={item.id}
        title={item.name}
        subtitle={item.description}
        icon={getSkillIcon(item.category)}
        actions={
          <div style={{ display: 'flex', gap: '8px' }}>
            {!isSystemTemplate && (
              <>
                <ViewAction size="xs" onClick={() => navigate(`${basePath}/${item.id}`)} title="Inspect Skill" />
                <EditAction size="xs" onClick={() => navigate(`${basePath}/${item.id}/edit`)} title="Modify Skill" />
                <DeleteAction size="xs" onClick={() => setShowDeleteModal(item)} title="Remove Skill" />
              </>
            )}
            {isSystemTemplate && (
              <>
                <ViewAction size="xs" onClick={() => navigate(`${basePath}/${item.id}`)} title="View Template" />
                {!isClubAdmin && (
                  <>
                    <EditAction size="xs" onClick={() => navigate(`${basePath}/${item.id}/edit`)} title="Edit Template" />
                    <DeleteAction size="xs" onClick={() => setShowDeleteModal({ ...item, isTemplate: true })} title="Delete Template" />
                  </>
                )}
                {isClubAdmin && !alreadyCopied && (
                  <CopyAction size="xs" label="Copy to Club" onClick={() => handleCopyTemplate(item)} title="Copy to Club" />
                )}
              </>
            )}
          </div>
        }
      >
        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border-light)' }}>
          <AttributeGrid columns={3} gap="2rem">
            <AttributeStack
              label="Performance Tier"
              value={
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={14} style={{ opacity: 0.5 }} />
                  {getDifficultyLabel(item.difficulty_level)} Complexity
                </span>
              }
            />
            <AttributeStack
              label="Skill Domain"
              value={
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <BookOpen size={14} style={{ opacity: 0.5 }} />
                  {getCategoryLabel(item.category)}
                </span>
              }
            />
            <AttributeStack
              label="Status"
              value={
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {isSystemTemplate && (
                    <Badge variant="outline" size="sm">
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Lock size={12} /> SYSTEM TEMPLATE
                      </span>
                    </Badge>
                  )}
                  {alreadyCopied && (
                    <Badge variant="success" size="sm">COPIED</Badge>
                  )}
                  {!isSystemTemplate && (
                    <Badge variant="outline" size="sm">
                      {item.category?.replace('_', ' ').toUpperCase()}
                    </Badge>
                  )}
                  {item.is_active ? (
                    <Badge variant="success" size="sm">ACTIVE</Badge>
                  ) : (
                    <Badge variant="warning" size="sm">INACTIVE</Badge>
                  )}
                </div>
              }
            />
          </AttributeGrid>
        </div>
      </ListingItem>
    );
  };

  const pageBackPath = isClubAdmin ? '/admin/club' : '/admin/system-templates';

  return (
    <AdminPageLayout
      title="SKILL MANAGEMENT"
      subtitle={isClubAdmin ? "Manage system templates and custom club skills for player development" : "Manage platform-level skill templates"}
      backPath={pageBackPath}
      maxWidth="1400px"
      primaryActions={
        isClubAdmin
          ? <CreateAction onClick={() => navigate(`${basePath}/create`)} label="New Skill" />
          : <CreateAction onClick={() => navigate(`${basePath}/create`)} label="New Template" />
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
        {/* Club Custom Skills - Only show in club context */}
        {isClubAdmin && (
          <Section title="CLUB CUSTOM SKILLS">
            <Listing
              loading={loading}
              items={filteredClubSkills}
              filters={filterBar}
              variant="list"
              renderItem={(skill) => renderSkillItem(skill, false)}
              emptyMessage="No custom skills created yet. Copy from system templates or create from scratch."
            />
          </Section>
        )}

        {/* System Templates */}
        <Section title="SYSTEM TEMPLATES">
          <Listing
            loading={loading}
            items={filteredSystemTemplates}
            filters={filterBar}
            variant="list"
            renderItem={(template) => renderSkillItem(template, true)}
            emptyMessage="No system templates available"
          />
        </Section>
      </div>

      {showDeleteModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowDeleteModal(null)}
          onConfirm={() => handleDeleteSkill(showDeleteModal)}
          title={showDeleteModal.isTemplate ? "Delete Skill Template" : "Remove Club Skill"}
          variant="danger"
          confirmText="Confirm Deletion"
          loading={isDeleting}
        >
          <p>
            Confirm deletion of the <strong>{showDeleteModal.name}</strong> {showDeleteModal.isTemplate ? 'skill template' : 'club skill'}.
          </p>
          <p style={{ marginTop: '0.5rem', color: 'var(--color-error)', fontWeight: 600, fontSize: '0.9rem' }}>
            {showDeleteModal.isTemplate
              ? 'Warning: This platform-level template will be permanently deleted. Clubs that have copied this template will not be affected.'
              : 'Warning: This skill will be removed from your club\'s skill library. Existing assignments using this skill will not be affected.'}
          </p>
        </ConfirmDialog>
      )}
    </AdminPageLayout>
  );
};

export default SkillTemplateManagement;
